import { useMemo, useState } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Button } from '@/components/atoms/button';
import { EntityIndexLayout, WorkspaceShell, useWorkspaceHeader } from '@/components/layouts';
import { RoleListPanel } from '@/components/organisms/settings-roles/RoleListPanel';
import { RoleTableView } from '@/components/organisms/settings-roles/RoleTableView';
import { useCloneRole } from '@/hooks/settings/useCloneRole';
import { useDeleteRole } from '@/hooks/settings/useDeleteRole';
import { useHasPermission } from '@/hooks/usePermissions';
import type { RoleListItem } from '@/types/settings';

export const Route = createFileRoute('/_dashboard/dashboard/settings/permissions')({
  component: RolesPage,
});

// Manual pathname derivation mirrors the users mold (regex/string matching
// rather than useEntityRouteState).
const ROLE_DETAIL_ROUTE_PATTERN = /^\/dashboard\/settings\/permissions\/([^/]+)\/?$/;
const ROLE_EDIT_ROUTE_PATTERN = /^\/dashboard\/settings\/permissions\/([^/]+)\/edit\/?$/;
const ROLE_NEW_ROUTE_PATTERN = /^\/dashboard\/settings\/permissions\/new\/?$/;
const ROLE_INDEX_ROUTE_PATTERN = /^\/dashboard\/settings\/permissions\/?$/;

function RolesPage() {
  const router = useRouter();
  const { location } = useRouterState();
  const deleteRole = useDeleteRole();
  const cloneRole = useCloneRole();
  const canCreateRoles = useHasPermission('roles:create');
  const canEditRoles = useHasPermission('roles:edit');
  const canDeleteRoles = useHasPermission('roles:delete');

  const [deleteTarget, setDeleteTarget] = useState<RoleListItem | null>(null);

  const detailRouteRoleId = location.pathname.match(ROLE_DETAIL_ROUTE_PATTERN)?.[1];
  const detailRoleId = detailRouteRoleId && detailRouteRoleId !== 'new' ? detailRouteRoleId : null;
  const editRoleId = location.pathname.match(ROLE_EDIT_ROUTE_PATTERN)?.[1];
  const selectedId = editRoleId ?? detailRoleId ?? undefined;
  const isNewRoute = ROLE_NEW_ROUTE_PATTERN.test(location.pathname);
  const isEditRoute = Boolean(editRoleId);
  const isFormMode = isNewRoute || isEditRoute;
  const hasChildRoute = Boolean(selectedId) || isNewRoute;
  const isIndexRoute = ROLE_INDEX_ROUTE_PATTERN.test(location.pathname);

  function openDetail(roleId: string) {
    void router.navigate({ to: '/dashboard/settings/permissions/$id', params: { id: roleId } });
  }

  // Auto-select (split desktop) replaces history so Back doesn't loop.
  function autoSelectDetail(roleId: string) {
    void router.navigate({
      to: '/dashboard/settings/permissions/$id',
      params: { id: roleId },
      replace: true,
    });
  }

  function openEdit(roleId: string) {
    if (!canEditRoles) return;
    void router.navigate({
      to: '/dashboard/settings/permissions/$id/edit',
      params: { id: roleId },
    });
  }

  function handleClone(role: RoleListItem) {
    if (!canCreateRoles) return;
    cloneRole.mutate(role.id, {
      onSuccess: (cloned) => openEdit(cloned.id),
    });
  }

  return (
    <WorkspaceShell hideHeader={isFormMode}>
      <RolesHeaderSlot
        hidden={isFormMode}
        showTitleFallback={!isFormMode && !selectedId}
        canCreateRoles={canCreateRoles}
        onNewRole={() => {
          if (!canCreateRoles) return;
          void router.navigate({ to: '/dashboard/settings/permissions/new' });
        }}
      />

      <EntityIndexLayout
        title="Roles"
        storageKey="roles-view"
        hasChildRoute={hasChildRoute}
        onResetToIndex={() => void router.navigate({ to: '/dashboard/settings/permissions' })}
        viewToggleShowLabels
        viewToggleActiveTone="muted"
        splitWidthClass="w-[360px]"
        tableSheetWidthClass="w-full sm:max-w-4xl"
        hideListInSplit={isFormMode}
        fullPageFormInTable
        splitList={() => (
          <div className="flex h-full flex-col">
            <RoleListPanel
              selectedId={selectedId}
              onSelect={openDetail}
              onAutoSelect={autoSelectDetail}
              isIndexRoute={isIndexRoute}
            />
          </div>
        )}
        splitDetail={<Outlet />}
        tableView={({ openInSheet }) => (
          <RoleTableView
            canEdit={canEditRoles}
            canClone={canCreateRoles}
            canDelete={canDeleteRoles}
            clonePending={cloneRole.isPending}
            onView={(roleId) => openInSheet(() => openDetail(roleId))}
            onEdit={(roleId) => openEdit(roleId)}
            onClone={(role) => handleClone(role)}
            onDelete={(role) => setDeleteTarget(role)}
          />
        )}
        sheetContent={<Outlet />}
        mobileContent={
          <div className="flex h-full flex-col md:hidden">
            {isIndexRoute ? (
              <RoleListPanel
                selectedId={selectedId}
                onSelect={openDetail}
                onAutoSelect={autoSelectDetail}
                isIndexRoute={isIndexRoute}
              />
            ) : (
              <Outlet />
            )}
          </div>
        }
      />

      <Dialog
        open={canDeleteRoles && !!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete role?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {deleteTarget ? (
                <>
                  Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
                  {deleteTarget.membershipCount > 0 ? (
                    <>
                      {' '}
                      This role is assigned to {deleteTarget.membershipCount} user(s), remove it
                      from all users first.
                    </>
                  ) : null}
                </>
              ) : null}
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deleteRole.isPending}
              onClick={() => setDeleteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                deleteRole.isPending ||
                Boolean(deleteTarget?.isSystem) ||
                (deleteTarget?.membershipCount ?? 0) > 0
              }
              onClick={() => {
                if (!deleteTarget) return;
                deleteRole.mutate(deleteTarget.id, {
                  onSuccess: () => setDeleteTarget(null),
                });
              }}
            >
              {deleteRole.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WorkspaceShell>
  );
}

function RolesHeaderSlot({
  hidden,
  showTitleFallback,
  canCreateRoles,
  onNewRole,
}: {
  hidden: boolean;
  showTitleFallback: boolean;
  canCreateRoles: boolean;
  onNewRole: () => void;
}) {
  const newButton = useMemo(() => {
    if (hidden || !canCreateRoles) return null;
    return (
      <Button size="sm" title="Add role" onClick={onNewRole}>
        <Plus size={14} />
        Add role
      </Button>
    );
  }, [canCreateRoles, hidden, onNewRole]);

  // Fallback franja title when no role is selected (table view, or the brief
  // index moment). When a role is open the detail route's own slot supplies the
  // richer context, taking over this key.
  const contextHeader = useMemo(() => {
    if (!showTitleFallback) return null;
    return <h1 className="truncate text-base font-semibold">Roles</h1>;
  }, [showTitleFallback]);

  useWorkspaceHeader({ newButton, contextHeader }, [newButton, contextHeader]);
  return null;
}
